<template>
  <data-manager-layout>
    <template slot="actions">
      <el-button
        v-if="!readonly"
        type="primary"
        icon="el-icon-s-tools"
        @click="manageUUIDs"
      >
        {{ $t('DataManager_Button_ManageUUIDs') }}
      </el-button>

      <el-button
        v-if="!readonly"
        type="primary"
        icon="el-icon-circle-plus"
        @click="addLocation"
      >
        {{ $t('DataManager_Button_CreateLocation') }}
      </el-button>
    </template>

    <rest-list :payload="fetchEndpoint" ref="rest-list">
      <rest-list-column
        v-if="selectedColumns.title"
        prop="title"
        :title="$t('DataManager_Locations_Field_Title')"
        :route="!readonly ? rowUpdateRoute : rowViewRoute"
      />

      <rest-list-column
        v-if="selectedColumns.address"
        prop="address"
        :title="$t('DataManager_Locations_Field_Address')"
        :tooltip="true"
      />

      <rest-list-column-enum
        v-if="selectedColumns.type"
        prop="type"
        :title="$t('DataManager_Locations_Field_Type')"
        :enum="DataManagerLocationsTypesLabels"
      />

      <rest-list-column
        v-if="selectedColumns.tags"
        prop="tags"
        :title="$t('DataManager_Locations_Field_Tags')"
        :sortable="false"
      >
        <template slot-scope="scope">
          <overflow-tooltip :value="tagsFormatter(scope.row.tags)" />
        </template>
      </rest-list-column>

      <rest-list-column
        v-if="selectedColumns.ibeacon_uuid"
        prop="ibeacon_uuid"
        :title="$t('DataManager_Locations_Field_UUID')"
        :tooltip="true"
      />

      <rest-list-column
        v-if="selectedColumns.ibeacon_major"
        prop="ibeacon_major"
        :title="$t('DataManager_Locations_Field_iBeaconMajor')"
      />

      <rest-list-column
        v-if="selectedColumns.ibeacon_minor"
        prop="ibeacon_minor"
        :title="$t('DataManager_Locations_Field_iBeaconMinor')"
      />
      <rest-list-column-actions>
        <template slot="header" slot-scope="{}">
          <rest-list-column-settings
            storage-key="datamanager-locations-list-columns"
            :title="$t('DataManager_Locations_Field_Settings')"
            :columns="columns"
            v-model="selectedColumns"
          />
        </template>
        <template slot-scope="scope">
          <rest-list-action-view
            v-if="readonly"
            :route="rowViewRoute(scope.row)"
          />
          <div v-else>
            <rest-list-action-update :route="rowUpdateRoute(scope.row)" />
            <rest-list-action-delete
              :delete-message="
                $t('DataManager_Locations_Message_DeleteLocation')
              "
              :endpoint="rowDeleteEndpoint(scope.row)"
            />
          </div>
        </template>
      </rest-list-column-actions>
    </rest-list>
  </data-manager-layout>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import OverflowTooltip from '@/common/components/form/OverflowTooltip'
import RestListColumnSettings from '@/common/components/rest/RestListColumnSettings'
import RestListActionView from '@/common/components/rest/RestListActionView'
import RestListActionUpdate from '@/common/components/rest/RestListActionUpdate'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import DataManagerLayout from '@/modules/data-manager/pages/DataManagerLayout'
import { DataManagerLocationsTypesLabels } from '@/modules/data-manager/enum/LocationTypeEnum'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'

export default {
  name: 'DataManagerLocationsPage',

  components: {
    RestListColumnActions,
    DataManagerLayout,
    RestList,
    RestListColumn,
    OverflowTooltip,
    RestListColumnSettings,
    RestListActionView,
    RestListActionDelete,
    RestListActionUpdate,
    RestListColumnEnum
  },

  data() {
    return {
      selectedColumns: {},
      DataManagerLocationsTypesLabels
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `locations`,
        params: {
          sort: 'title'
        }
      })
    },

    rowAddEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `locations`
      })
    },

    columns() {
      return {
        title: {
          title: this.$t('DataManager_Locations_Field_Title'),
          defaultValue: true,
          validation: true
        },
        address: {
          title: this.$t('DataManager_Locations_Field_Address'),
          defaultValue: true,
          validation: true
        },
        type: {
          title: this.$t('DataManager_Locations_Field_Type'),
          defaultValue: true,
          validation: true
        },
        tags: {
          title: this.$t('DataManager_Locations_Field_Tags'),
          defaultValue: true,
          validation: true
        },
        ibeacon_uuid: {
          title: this.$t('DataManager_Locations_Field_UUID'),
          defaultValue: false,
          validation: true
        },
        ibeacon_major: {
          title: this.$t('DataManager_Locations_Field_iBeaconMajor'),
          defaultValue: false,
          validation: true
        },
        ibeacon_minor: {
          title: this.$t('DataManager_Locations_Field_iBeaconMinor'),
          defaultValue: false,
          validation: true
        }
      }
    },

    readonly() {
      return !this.$can('manageProjectTechnical')
    }
  },

  methods: {
    addLocation() {
      this.$router.push(
        projectRoute({
          name: 'DataManagerLocationsCreate'
        })
      )
    },

    manageUUIDs() {
      this.$router.push(
        projectRoute({
          name: 'IBeaconsUUIDs'
        })
      )
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `locations/${row.id}`
      })
    },

    rowUpdateRoute(row) {
      return projectRoute({
        name: 'DataManagerLocationsEdit',
        query: { id: row.id }
      })
    },

    rowViewRoute(row) {
      return projectRoute({
        name: 'DataManagerLocationsView',
        query: { id: row.id }
      })
    },

    tagsFormatter(tags) {
      if (tags.length === 0 || tags.filter(item => item !== '').length === 0) {
        return '-'
      } else {
        return `#${tags.filter(item => item !== '').join(' #')}`
      }
    }
  }
}
</script>

<style scoped>
.controls {
  margin-right: 0;
}
</style>



// WEBPACK FOOTER //
// src/modules/data-manager/pages/DataManagerLocationsPage.vue